import { useContext, useState as classicUseState } from 'react';
import { useState } from '@hookstate/core';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { format } from 'date-fns';

import ReactTooltip from 'react-tooltip';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  UpdateStatusPriority,
  UpdateStatusType,
} from '@models/UpdateStatus.model';
import { changeUpdateStatus } from '@services/queries/updates/changeUpdateStatus/changeUpdateStatus';
import {
  getDefaultTagBackground,
  getTagFontColor,
  getUpdateFlagColor,
  setAttr,
} from '@utils';

import { AffectedItemsDialog } from '@components/UpdatesPageMenus/Components/AffectedItemsDialog/AffectedItemsDialog';
import { MarkAsCompleteDialog } from '@components/UpdatesPageMenus/Components/MarkAsCompleteDialog/MarkAsCompleteDialog';
import { PriorityDialog } from '@components/UpdatesPageMenus/Components/PriorityDialog/PriorityDialog';
import { ReAssignDialog } from '@components/UpdatesPageMenus/Components/ReAssignDialog/ReAssignDialog';

import {
  IUpdateRawQueryList,
  IUpdateRawQueryListApiModel,
} from '@models/Update.model';
import { changeUpdateStatusByUpdate } from '@services/queries/updates';
import { NavigationContext } from '@layout/navigation/navigation';
import { useToast } from '@hooks';

import { SetState } from '@pages/Organization/utils/onOrgNameSubmit';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { BreakingText } from '@components/Text/TruncatedText';
import { CategoryColor } from '@models/TagCategory.model';
import { TagContainer } from '@pages/Updates/components/UpdatesNew/Updates.styles';
import TextComponent from '@components/Text/Text';
import { UpdateDescription } from './components/UpdateDescription/UpdateDescription';
import {
  Close,
  Header,
  Text,
  SubjectDivider,
  QuickActionsDiv,
  TagsDiv,
  AssetsCountSquare,
  // Tags,
  // WidthDivider,
  Indicator,
  LabelText,
  Label,
  Item,
  Tabs,
  TabsContainer,
  Back,
  AssetImgContainer,
  HeaderLeft,
  HeaderRight,
  TagsContainer,
  Assignee,
  // Subject,
  // Icon2,
  TagsRight,
  HeaderRightButtons,
} from './DetailsDialog.styles';

const UpdateFlags = ({ priority }: IUpdateRawQueryList) => {
  return (
    // <IconContainer>
    <BlueIcon icon="flag" color={getUpdateFlagColor(priority)} />
    // </IconContainer>
  );
};

const buttonList = [
  'Dismiss',
  'Mark as Complete',
  'Prioritize',
  'Priority Flags',
  'Not Done',
  'Assignee',
  'Return to Prioritized',
  'Return to New',
] as const;
type ButtonList = typeof buttonList;
type ButtonTypes = ButtonList[number];
interface IDetailsDialog {
  onClose: () => void;
  updateStatus: IUpdateRawQueryList;
}

const UPDATES_DATE_FORMAT = 'MMM do yyyy';

export const DetailsDialogRaw = ({ onClose, updateStatus }: IDetailsDialog) => {
  const params = useParams<{ id: string; equipmentId: string }>();

  const { toast } = useToast();

  const { studioId } = useContext(NavigationContext);

  const page = useState<'description' | 'update-notes' | 'related-notes'>(
    'description',
  );

  const queryClient = useQueryClient();

  const reloadQueries = async () => {
    queryClient.invalidateQueries(['updateStatuses', updateStatus.id]);

    // dispatch(addUpdatesLoadingUpdateId(updateStatus.id));
    // await sleep(updatesMtViewTime * 1000);
    // dispatch(removeUpdatesLoadingUpdateId(updateStatus.id));

    queryClient.invalidateQueries(['updateStatuses']);
    queryClient.invalidateQueries(['updatesMetrics']);
    queryClient.invalidateQueries(['studios', params.id, 'updates']);
    queryClient.invalidateQueries([
      'equipments',
      params.equipmentId,
      'updateStatuses',
    ]);
  };

  const mutationByUpdate = useMutation(changeUpdateStatusByUpdate, {
    onSuccess: async () => {
      toast.success('Update changed successfully');

      reloadQueries();
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const mutation = useMutation(changeUpdateStatus, {
    onSuccess: async () => {
      toast.success('Update changed successfully');

      reloadQueries();
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const [openMarkAsCompleteDialog, setOpenMarkAsCompleteDialog] =
    classicUseState<boolean>(false);
  const [openPriorityDialog, setOpenPriorityDialog] =
    classicUseState<boolean>(false);
  const [openReAssignDialog, setOpenReAssignDialog] =
    classicUseState<boolean>(false);
  const [openAffectedItemsDialog, setOpenAffectedItemsDialog] =
    classicUseState<boolean>(false);

  const { isMobile, isDesktop } = useSelector(checkDevice());

  return (
    <>
      <Header>
        <HeaderLeft>
          {updateStatus.date && (
            <BreakingText
              text={isMobile ? 'p100' : 'p200'}
              textColor="n700"
              lineClamp={2}
            >
              {format(updateStatus.date, UPDATES_DATE_FORMAT)}
            </BreakingText>
          )}

          <AssetImgContainer>
            {(() => {
              if (updateStatus.usStudioId) {
                return (
                  <BreakingText textColor="b401" text="b200" lineClamp={2}>
                    INTERNAL
                  </BreakingText>
                );
              }

              if (updateStatus.type === 'products') {
                return (
                  <BreakingText text="p200" textColor="n900" lineClamp={2}>
                    {updateStatus.pManufacturer}
                  </BreakingText>
                );
              }

              return '';
            })()}
          </AssetImgContainer>
        </HeaderLeft>

        <HeaderRight>
          <HeaderRightButtons className="new-icon">
            {buttonList.map(btn => {
              const clickHandlerParams = {
                mutation,
                mutationByUpdate,
                updateStatus,
                studioId,
                setOpenMarkAsCompleteDialog,
                setOpenPriorityDialog,
                setOpenReAssignDialog,
                type: btn,
              };
              return (
                <>
                  <UpdateActionButtions
                    clickHandler={getClickHandler(clickHandlerParams)}
                    updateStatus={updateStatus}
                    type={btn}
                  />
                </>
              );
            })}
          </HeaderRightButtons>
          <Close onClick={onClose}>
            <BlueIcon icon="cross" color="#8F95B2" />
          </Close>
        </HeaderRight>
      </Header>
      <SubjectDivider />
      <BreakingText
        text={isMobile ? 'h400' : 'h600'}
        textColor="n902"
        lineClamp={2}
      >
        {updateStatus.subject}
      </BreakingText>
      <TagsDiv>
        <AssetsCountSquare
          className="new-icon"
          onClick={() => {
            if (isMobile) return;
            setOpenAffectedItemsDialog(true);
          }}
        >
          <BlueIcon icon="cube" color="#696f8c" size={12} />
          <TextComponent text={isMobile ? 'h300' : 'h400'} textColor="n800">
            {updateStatus.affects}
          </TextComponent>
        </AssetsCountSquare>

        {/* <WidthDivider width="16px" /> */}

        <TagsContainer>
          {updateStatus.tags?.map(tag => (
            <TagContainer
              text={isMobile ? 'h300' : 'b200'}
              bg={getDefaultTagBackground(tag.color as CategoryColor)}
              key={tag.id}
              c={getTagFontColor(tag.color)}
            >
              {tag.name}
            </TagContainer>
            // <Tags
            //   key={tag.name}
            //   background={tag.color}
            //   color={getTagFontColor(tag.color)}
            // >
            //   {tag.name}
            // </Tags>
          ))}
          <TagsRight />
        </TagsContainer>
      </TagsDiv>
      <TabsContainer>
        <Tabs>
          <Item
            onClick={() => {
              page.set('description');
            }}
          >
            <Label>
              <LabelText
                isActive={page.get() === 'description'}
                className="tabs-text"
              >
                Description
              </LabelText>
            </Label>

            <Indicator isActive={page.get() === 'description'} />
          </Item>
        </Tabs>
        <Back />
      </TabsContainer>
      <UpdateDescription
        description={updateStatus.description}
        originalMessage={updateStatus.message}
      />
      <MarkAsCompleteDialog
        open={openMarkAsCompleteDialog}
        onClose={() => {
          setOpenMarkAsCompleteDialog(false);
        }}
        updateStatusId={updateStatus.usId}
        updateId={updateStatus.id}
      />
      <PriorityDialog
        open={openPriorityDialog}
        onClose={() => {
          setOpenPriorityDialog(false);
        }}
        updateStatus={updateStatus}
        updateStatusId={updateStatus.usId}
        updateId={updateStatus.id}
      />
      <ReAssignDialog
        open={openReAssignDialog}
        onClose={() => {
          setOpenReAssignDialog(false);
        }}
        assigneeId={updateStatus.assignee}
        updateId={updateStatus.id}
        updateStatusId={updateStatus.usId}
      />
      {openAffectedItemsDialog && isDesktop && (
        <AffectedItemsDialog
          open={openAffectedItemsDialog}
          onClose={() => {
            setOpenAffectedItemsDialog(false);
          }}
          updateId={updateStatus.id}
        />
      )}
    </>
  );
};

interface ClickHandlerParams {
  mutation: UseMutationResult<
    IUpdateRawQueryListApiModel,
    unknown,
    Parameters<typeof changeUpdateStatus>['0'],
    unknown
  >;
  mutationByUpdate: UseMutationResult<
    IUpdateRawQueryListApiModel,
    unknown,
    Parameters<typeof changeUpdateStatusByUpdate>['0'],
    unknown
  >;
  updateStatus: IUpdateRawQueryList;
  studioId: string;
  setOpenMarkAsCompleteDialog: SetState<boolean>;
  setOpenPriorityDialog: SetState<boolean>;
  setOpenReAssignDialog: SetState<boolean>;
  type: ButtonTypes;
}
function getClickHandler(params: ClickHandlerParams) {
  const {
    mutation,
    mutationByUpdate,
    updateStatus,
    studioId,
    setOpenMarkAsCompleteDialog,
    setOpenPriorityDialog,
    setOpenReAssignDialog,
    type,
  } = params;

  const dismissClick = () => {
    if (!mutation.isLoading && !mutationByUpdate.isLoading) {
      if (updateStatus.usId) {
        mutation.mutate({
          updateStatusId: updateStatus.usId,
          type: UpdateStatusType.DISMISSED,
        });
      } else {
        mutationByUpdate.mutate({
          studioId,
          updateId: updateStatus.id,
          type: UpdateStatusType.DISMISSED,
        });
      }
    }
  };
  const markAsCompleteClick = () => {
    setOpenMarkAsCompleteDialog(true);
  };

  const prioritizeClick = () => setOpenPriorityDialog(true);
  const priorityFlagsClick = () => setOpenPriorityDialog(true);

  const notDoneClick = () => {
    if (!mutation.isLoading && !mutationByUpdate.isLoading) {
      if (updateStatus.usId) {
        mutation.mutate({
          updateStatusId: updateStatus.usId,
          type: UpdateStatusType.PRIORITIZED,
        });
      } else {
        mutationByUpdate.mutate({
          studioId,
          updateId: updateStatus.id,
          type: UpdateStatusType.PRIORITIZED,
        });
      }
    }
  };
  const assigneeClick = () => setOpenReAssignDialog(true);

  const returnToPrioritizedClick = () => {
    if (!mutation.isLoading && !mutationByUpdate.isLoading) {
      if (updateStatus.usId) {
        mutation.mutate({
          updateStatusId: updateStatus.usId,
          type: UpdateStatusType.PRIORITIZED,
        });
      } else {
        mutationByUpdate.mutate({
          studioId,
          updateId: updateStatus.id,
          type: UpdateStatusType.PRIORITIZED,
        });
      }
    }
  };
  const returnToNewClick = () => {
    if (!mutation.isLoading && !mutationByUpdate.isLoading) {
      if (updateStatus.usId) {
        mutation.mutate({
          updateStatusId: updateStatus.usId,
          type: UpdateStatusType.NEW,
        });
      } else {
        mutationByUpdate.mutate({
          studioId,
          updateId: updateStatus.id,
          type: UpdateStatusType.NEW,
        });
      }
    }
  };

  switch (type) {
    case 'Dismiss':
      return dismissClick;
    case 'Mark as Complete':
      return markAsCompleteClick;
    case 'Prioritize':
      return prioritizeClick;
    case 'Priority Flags':
      return priorityFlagsClick;
    case 'Not Done':
      return notDoneClick;
    case 'Assignee':
      return assigneeClick;

    case 'Return to Prioritized':
      return returnToPrioritizedClick;

    case 'Return to New':
      return returnToNewClick;
    default:
      // Should never happen
      return type;
  }
}

interface UpdateActionButtonsProps {
  clickHandler: VoidFunction;
  updateStatus: IUpdateRawQueryList;
  type: ButtonTypes;
}

function UpdateActionButtions(props: UpdateActionButtonsProps) {
  const { clickHandler, updateStatus, type } = props;

  const { isDesktop, isMobile } = useSelector(checkDevice());

  const dismissJsx = (
    <QuickActionsDiv onClick={clickHandler}>
      {/* <Icon> */}
      <BlueIcon icon="delete" color="#8F95B2" />
      {/* </Icon> */}
      <Text className="small">Dismiss</Text>
    </QuickActionsDiv>
  );

  const markAsCompleteJsx = (
    <QuickActionsDiv onClick={clickHandler}>
      {/* <Icon> */}
      <BlueIcon icon="tick-circle" color="#8F95B2" />
      {/* </Icon> */}
      <Text className="small">Mark as Complete</Text>
    </QuickActionsDiv>
  );

  const prioritizeJsx = (
    <QuickActionsDiv onClick={clickHandler}>
      {/* <Icon> */}
      <BlueIcon icon="flag" color="#8F95B2" />
      {/* </Icon> */}
      <Text className="small">Prioritize</Text>
    </QuickActionsDiv>
  );

  const priorityFlagsJsx = (
    <QuickActionsDiv disabled={setAttr(isMobile)} onClick={clickHandler}>
      {UpdateFlags(updateStatus)}
      {isDesktop && (
        <Text className="small">{formatUpdateStatus(updateStatus)}</Text>
      )}
    </QuickActionsDiv>
  );

  const notDoneJsx = (
    <QuickActionsDiv onClick={clickHandler}>
      {/* <Icon> */}
      <BlueIcon icon="ban-circle" color="#8F95B2" />
      {/* </Icon> */}
      <Text className="small">Not Done</Text>
    </QuickActionsDiv>
  );

  const assigneeJsx = (
    <QuickActionsDiv
      disabled={setAttr(isMobile)}
      hidden={setAttr(isMobile && !updateStatus.aAvatar)}
      className="last-action"
      onClick={clickHandler}
    >
      {updateStatus.aAvatar ? (
        <Assignee src={updateStatus.aAvatar} data-tip data-for="helpTooltip" />
      ) : (
        // <Icon>
        <BlueIcon icon="new-person" color="#8F95B2" />
        // </Icon>
      )}
      {!updateStatus.aName && <Text className="small">Assign</Text>}
      <ReactTooltip
        className="tooltip"
        id="helpTooltip"
        effect="solid"
        place="bottom"
        multiline
      >
        {updateStatus.aName}
      </ReactTooltip>
    </QuickActionsDiv>
  );

  const returnToPrioritizedJsx = (
    <QuickActionsDiv className="last-action" onClick={clickHandler}>
      {/* <Icon> */}
      <BlueIcon icon="reset" color="#8F95B2" />
      {/* </Icon> */}
      <Text className="small">Return to Prioritized</Text>
    </QuickActionsDiv>
  );

  const returnToNewJsx = (
    <QuickActionsDiv className="last-action" onClick={clickHandler}>
      {/* <Icon> */}
      <BlueIcon icon="reset" color="#8F95B2" />
      {/* </Icon> */}
      <Text className="small">Return to New</Text>
    </QuickActionsDiv>
  );
  if (type === 'Dismiss') {
    return (
      <>
        {[UpdateStatusType.NEW, UpdateStatusType.PRIORITIZED, null].includes(
          updateStatus.usType,
        ) &&
          isDesktop &&
          dismissJsx}
      </>
    );
  }
  if (type === 'Mark as Complete') {
    return (
      <>
        {[UpdateStatusType.PRIORITIZED].includes(updateStatus.usType) &&
          isDesktop &&
          markAsCompleteJsx}
      </>
    );
  }

  if (type === 'Prioritize') {
    return (
      <>
        {[UpdateStatusType.NEW, null].includes(updateStatus.usType) &&
          isDesktop &&
          prioritizeJsx}
      </>
    );
  }
  if (type === 'Priority Flags') {
    return (
      <>
        {[UpdateStatusType.PRIORITIZED].includes(updateStatus.usType) &&
          updateStatus.priority &&
          priorityFlagsJsx}
      </>
    );
  }

  if (type === 'Not Done') {
    return (
      <>
        {[UpdateStatusType.COMPLETED].includes(updateStatus.usType) &&
          isDesktop &&
          notDoneJsx}
      </>
    );
  }

  if (type === 'Assignee') {
    return (
      <>
        {[UpdateStatusType.PRIORITIZED].includes(updateStatus.usType) &&
          assigneeJsx}
      </>
    );
  }

  if (type === 'Return to Prioritized') {
    return (
      <>
        {[UpdateStatusType.DISMISSED].includes(updateStatus.usType) &&
          updateStatus.priority &&
          isDesktop &&
          returnToPrioritizedJsx}
      </>
    );
  }

  if (type === 'Return to New') {
    return (
      <>
        {[UpdateStatusType.DISMISSED].includes(updateStatus.usType) &&
          !updateStatus.priority &&
          isDesktop &&
          returnToNewJsx}
      </>
    );
  }

  // Should never happen
  return type;
}
function formatUpdateStatus(updateStatus: IUpdateRawQueryList) {
  return updateStatus.priority === UpdateStatusPriority.NONCRITICAL
    ? 'Non-critical'
    : updateStatus.priority
        ?.charAt(0)
        .toUpperCase()
        .concat(updateStatus.priority?.slice(1));
}
